// EmailSendPrimitives.jsx — extracted primitives from EmailSendDashboard.jsx
// Phase 27c Housekeeping (F.1) · frontend split to keep main dashboard < 800 lines
// Zero functionality change · zero prop-shape change · public API is exactly
// what the main dashboard consumed inline before.

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    AlertTriangle, ArrowUpRight, Ban, Calendar, CheckCircle2, ChevronDown,
    ChevronRight, Clock, Eye, FlaskConical, History, Info, Key, Loader2,
    Mail, RefreshCw, Rocket, RotateCcw, Send, Shield, ShieldAlert, Sparkles,
    Timer, Users, X, Zap,
} from "lucide-react";


// ---- primitives -----------------------------------------------------------

export function KpiCard({ icon: Icon, label, value, tone = "primary", testid, hint }) {
    const toneClass = {
        primary: "text-[var(--color-primary)]",
        success: "text-[var(--color-success)]",
        alert: "text-[var(--color-alert)]",
        warning: "text-[#A3A3A3]",
        secondary: "text-[var(--color-secondary)]",
    }[tone];
    return (
        <div
            data-testid={testid}
            className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4"
        >
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-text-secondary mb-2">
                <Icon className={`h-3 w-3 ${toneClass}`} /> {label}
            </div>
            <div className={`text-2xl font-semibold tabular-nums ${toneClass}`}>
                {value}
            </div>
            {hint && (
                <p className="text-[10px] text-text-secondary mt-1 leading-tight">
                    {hint}
                </p>
            )}
        </div>
    );
}

export function EngineChip({ engine }) {
    const cls = {
        email_generator: "text-white bg-white/5 border-white/15",
        upgrade_engine: "text-[#A3A3A3] bg-[#A3A3A3]/10 border-[#A3A3A3]/30",
        credits_engine: "text-white bg-white/5 border-white/15",
        retention_engine: "text-white bg-white/5 border-white/15",
    }[engine] || "text-text-secondary bg-white/5 border-white/10";
    return (
        <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${cls}`}>
            {String(engine || "").replace("_engine", "").replace("_", " ")}
        </span>
    );
}

export function StatusBadge({ status }) {
    const cls = {
        acknowledged: "text-white bg-white/5 border-white/15",
        scheduled: "text-white bg-white/5 border-white/15",
        due: "text-[#A3A3A3] bg-[#A3A3A3]/10 border-[#A3A3A3]/30",
        sent: "text-white bg-white/5 border-white/15",
        sandbox_dropped: "text-white bg-white/5 border-white/15",
        partial: "text-[#A3A3A3] bg-[#A3A3A3]/10 border-[#A3A3A3]/30",
        failed: "text-[var(--color-alert)] bg-[var(--color-alert)]/10 border-[var(--color-alert)]/40",
        canceled: "text-text-secondary bg-white/5 border-white/10",
        retracted: "text-[var(--color-alert)] bg-[var(--color-alert)]/10 border-[var(--color-alert)]/40",
    }[status] || "text-text-secondary bg-white/5 border-white/10";
    return (
        <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${cls}`}>
            {status}
        </span>
    );
}


// ---- flag banner ---------------------------------------------------------

export function FlagBanner({ statusData, isAdmin, onToggleMaster, onToggleSandbox, busy }) {
    if (!statusData) {
        return (
            <div
                data-testid="email-send-banner-disabled"
                className="rounded-2xl border border-[var(--color-alert)]/40 bg-[var(--color-alert)]/10 p-4 md:p-5 flex items-center gap-3 mb-6"
            >
                <ShieldAlert className="h-6 w-6 text-[var(--color-alert)] shrink-0" />
                <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-[var(--color-alert)] font-semibold mb-1">
                        EMAIL_ENGINE_SEND_ENABLED · DISABLED
                    </div>
                    <p className="text-xs text-text-secondary">
                        Email Send Layer is OFF. All dispatch endpoints return 503. Real deliveries are blocked.
                    </p>
                </div>
                {isAdmin && (
                    <button
                        data-testid="email-send-enable-master"
                        onClick={onToggleMaster}
                        disabled={busy}
                        className="text-xs rounded-full bg-[var(--color-alert)] hover:bg-[var(--color-alert)]/90 text-white px-4 py-2 font-medium shrink-0 flex items-center gap-2 disabled:opacity-50"
                    >
                        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Zap className="h-3 w-3" />}
                        Enable now
                    </button>
                )}
            </div>
        );
    }
    const sandbox = !!statusData.sandbox_mode;
    return (
        <div
            data-testid={sandbox ? "email-send-banner-sandbox" : "email-send-banner-live"}
            className={`rounded-2xl border p-4 md:p-5 flex flex-col sm:flex-row sm:items-center gap-3 mb-6 ${
                sandbox
                    ? "border-white/15 bg-white/5"
                    : "border-[#A3A3A3]/40 bg-[#A3A3A3]/10"
            }`}
        >
            {sandbox
                ? <FlaskConical className="h-6 w-6 text-white shrink-0" />
                : <Rocket className="h-6 w-6 text-[#A3A3A3] shrink-0" />}
            <div className="flex-1 min-w-0">
                <div className={`text-[10px] uppercase tracking-widest font-semibold mb-1 ${sandbox ? "text-white" : "text-[#A3A3A3]"}`}>
                    {sandbox
                        ? "SANDBOX MODE · Emails are NOT delivered"
                        : "PRODUCTION MODE · Real emails will be sent"}
                </div>
                <p className="text-xs text-text-secondary">
                    {sandbox
                        ? "Dispatches log to email_send_logs with status=sandbox_dropped. Zero external API calls. Rate limit: "
                        : "Live delivery via Resend. Rate limit: "}
                    <span className="text-text-main">{statusData.rate_limit}/day/admin</span>
                    {" · Segment cap: "}
                    <span className="text-text-main">{statusData.segment_cap} recipients/call</span>
                </p>
            </div>
            {isAdmin && (
                <div className="flex gap-2 shrink-0">
                    <button
                        data-testid="email-send-toggle-sandbox"
                        onClick={onToggleSandbox}
                        disabled={busy}
                        className="text-xs rounded-full border border-[var(--color-border)] text-text-secondary hover:text-text-main px-3 py-2 flex items-center gap-1 disabled:opacity-50"
                    >
                        {sandbox ? <Rocket className="h-3 w-3" /> : <FlaskConical className="h-3 w-3" />}
                        {sandbox ? "Go live" : "Sandbox"}
                    </button>
                    <button
                        data-testid="email-send-disable-master"
                        onClick={onToggleMaster}
                        disabled={busy}
                        className="text-xs rounded-full border border-[var(--color-alert)]/40 text-[var(--color-alert)] hover:bg-[var(--color-alert)]/10 px-3 py-2 flex items-center gap-1 disabled:opacity-50"
                    >
                        <ShieldAlert className="h-3 w-3" /> Disable
                    </button>
                </div>
            )}
        </div>
    );
}


// ---- schedule modal helpers -----------------------------------------------

export function defaultScheduleISO(offsetMinutes = 15) {
    const dt = new Date(Date.now() + offsetMinutes * 60 * 1000);
    // to <input type=datetime-local> value
    const pad = (n) => String(n).padStart(2, "0");
    return (
        `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T` +
        `${pad(dt.getHours())}:${pad(dt.getMinutes())}`
    );
}


// ---- confirm modal --------------------------------------------------------

export function ConfirmModal({ open, title, subtitle, tone = "primary", confirmLabel,
                        onConfirm, onCancel, busy, children }) {
    const toneCls = {
        primary: "bg-white hover:bg-white/90 text-black",
        alert: "bg-[var(--color-alert)] hover:bg-[var(--color-alert)]/90 text-white",
        warning: "bg-[#A3A3A3] hover:bg-[#A3A3A3]/90 text-black",
        success: "bg-white hover:bg-white/90 text-black",
    }[tone];
    return (
        <AnimatePresence>
            {open && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm"
                        onClick={busy ? undefined : onCancel}
                    />
                    <motion.div
                        initial={{ opacity: 0, y: 20, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.98 }}
                        transition={{ duration: 0.18 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
                    >
                        <div
                            data-testid="email-send-confirm-modal"
                            className="pointer-events-auto w-full max-w-3xl rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
                        >
                            <div className="flex items-start justify-between px-5 py-4 border-b border-[var(--color-border)]">
                                <div>
                                    <h3 className="text-base md:text-lg font-semibold text-text-main">
                                        {title}
                                    </h3>
                                    {subtitle && (
                                        <p className="text-xs text-text-secondary mt-1">{subtitle}</p>
                                    )}
                                </div>
                                <button
                                    onClick={onCancel}
                                    disabled={busy}
                                    data-testid="email-send-confirm-close"
                                    className="text-text-secondary hover:text-text-main disabled:opacity-40"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                            <div className="p-5 overflow-y-auto flex-1">
                                {children}
                            </div>
                            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-[var(--color-border)] bg-[var(--color-bg)]/40">
                                <button
                                    onClick={onCancel}
                                    disabled={busy}
                                    data-testid="email-send-confirm-cancel"
                                    className="text-xs rounded-full border border-[var(--color-border)] text-text-secondary hover:text-text-main px-4 py-2 disabled:opacity-40"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={onConfirm}
                                    disabled={busy}
                                    data-testid="email-send-confirm-ok"
                                    className={`text-xs rounded-full px-5 py-2 font-medium flex items-center gap-2 disabled:opacity-50 ${toneCls}`}
                                >
                                    {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
                                    {confirmLabel}
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}


// ---- suggestion row -------------------------------------------------------

export function SuggestionRow({ suggestion, onDispatch, onSchedule, onPreview, disabled }) {
    return (
        <li
            data-testid={`email-send-suggestion-${suggestion.id}`}
            className="rounded-xl border border-[var(--color-border)] bg-[var(--color-panel-ai)] p-4"
        >
            <div className="flex items-start gap-3 flex-wrap sm:flex-nowrap">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                        <EngineChip engine={suggestion.engine_source} />
                        {suggestion.category && (
                            <span className="text-[10px] uppercase tracking-widest text-text-secondary">
                                {suggestion.category}
                            </span>
                        )}
                        {suggestion.target_user_count != null && (
                            <span className="text-[10px] uppercase tracking-widest text-text-secondary flex items-center gap-1">
                                <Users className="h-3 w-3" />
                                {suggestion.target_user_count} recipients
                            </span>
                        )}
                    </div>
                    <div className="text-sm text-text-main font-medium">
                        {suggestion.subject || "(no subject)"}
                    </div>
                    {suggestion.body_markdown && (
                        <p className="text-[11px] text-text-secondary mt-1 line-clamp-2">
                            {String(suggestion.body_markdown).replace(/\{\{[^}]+\}\}/g, "…").slice(0, 200)}
                        </p>
                    )}
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                    <button
                        data-testid={`email-send-dispatch-btn-${suggestion.id}`}
                        onClick={() => onDispatch(suggestion)}
                        disabled={disabled}
                        className="text-[11px] rounded-full bg-white hover:bg-[#EDEDED] text-black px-4 py-2 font-medium flex items-center gap-1 disabled:opacity-50"
                    >
                        <Send className="h-3 w-3" /> Send now
                    </button>
                    <button
                        data-testid={`email-send-schedule-btn-${suggestion.id}`}
                        onClick={() => onSchedule(suggestion)}
                        disabled={disabled}
                        className="text-[11px] rounded-full border border-[var(--color-border)] text-text-secondary hover:text-text-main px-3 py-1.5 flex items-center gap-1 disabled:opacity-40"
                    >
                        <Calendar className="h-3 w-3" /> Schedule…
                    </button>
                    <button
                        data-testid={`email-send-preview-btn-${suggestion.id}`}
                        onClick={() => onPreview(suggestion)}
                        className="text-[11px] rounded-full border border-[var(--color-border)] text-text-secondary hover:text-text-main px-3 py-1.5 flex items-center gap-1"
                    >
                        <Eye className="h-3 w-3" /> Preview
                    </button>
                </div>
            </div>
        </li>
    );
}


// ---- log row --------------------------------------------------------------

export function timeUntil(iso) {
    if (!iso) return "";
    const diff = new Date(iso) - new Date();
    if (diff <= 0) return "due now";
    const mins = Math.round(diff / 60000);
    if (mins < 60) return `in ${mins}m`;
    const hours = Math.round(mins / 60);
    if (hours < 24) return `in ${hours}h`;
    return `in ${Math.round(hours / 24)}d`;
}

export function LogRow({ log, onCancel, onDispatchDue, onRetract, disabled }) {
    const [expanded, setExpanded] = useState(false);
    const status = log.status || "";
    const canCancel = status === "scheduled" || status === "due";
    const canSendNow = status === "due" || status === "scheduled";
    const canRetract = (status === "sent" || status === "sandbox_dropped" || status === "partial") && !log.retracted;

    return (
        <li
            data-testid={`email-send-log-${log.id}`}
            className="rounded-xl border border-[var(--color-border)] bg-[var(--color-panel-ai)] p-3"
        >
            <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                        <StatusBadge status={status} />
                        <EngineChip engine={log.engine_source} />
                        {log.sandbox_mode && (
                            <span className="text-[10px] uppercase tracking-widest text-white flex items-center gap-1">
                                <FlaskConical className="h-3 w-3" /> sandbox
                            </span>
                        )}
                        {log.retracted && (
                            <span className="text-[10px] uppercase tracking-widest text-[var(--color-alert)]">
                                retracted
                            </span>
                        )}
                    </div>
                    <div className="text-xs text-text-secondary flex flex-wrap items-center gap-2">
                        <span className="font-mono">
                            {(log.dispatched_at || log.scheduled_at || log.created_at || "")
                                .slice(0, 19).replace("T", " ")} UTC
                        </span>
                        {log.status === "due" && log.scheduled_at && (
                            <span className="text-[#A3A3A3] flex items-center gap-1">
                                <Timer className="h-3 w-3" /> {timeUntil(log.scheduled_at)}
                            </span>
                        )}
                        {log.status === "scheduled" && log.scheduled_at && (
                            <span className="flex items-center gap-1">
                                <Timer className="h-3 w-3" /> {timeUntil(log.scheduled_at)}
                            </span>
                        )}
                        <span>· {log.recipients_count || 0} recipient(s)</span>
                        {log.compliance?.skipped_opted_out?.length > 0 && (
                            <span className="text-[#A3A3A3]">
                                · {log.compliance.skipped_opted_out.length} opted-out skipped
                            </span>
                        )}
                    </div>
                    <button
                        onClick={() => setExpanded((v) => !v)}
                        data-testid={`email-send-log-toggle-${log.id}`}
                        className="text-[10px] uppercase tracking-widest text-[var(--color-primary)] mt-2 flex items-center gap-1"
                    >
                        {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                        {expanded ? "Hide details" : "Show details"}
                    </button>
                    <AnimatePresence initial={false}>
                        {expanded && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.16 }}
                                className="overflow-hidden"
                            >
                                <div className="mt-2 space-y-2">
                                    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg)] p-3">
                                        <div className="text-[10px] uppercase tracking-widest text-text-secondary mb-1">
                                            Recipients snapshot ({(log.recipients_snapshot || []).length})
                                        </div>
                                        <ul className="text-[11px] text-text-secondary space-y-0.5 max-h-40 overflow-auto">
                                            {(log.recipients_snapshot || []).slice(0, 20).map((r, i) => (
                                                <li key={i} className="font-mono truncate">
                                                    {r.email || r.user_id}{" "}
                                                    {r.message_id && (
                                                        <span className="text-[var(--color-primary)]">
                                                            → {r.message_id.slice(0, 14)}
                                                        </span>
                                                    )}
                                                    {r.error && (
                                                        <span className="text-[var(--color-alert)]"> · {r.error.slice(0, 60)}</span>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                    {log.compliance && (
                                        <div className="rounded-lg border border-white/15 bg-white/5 p-3 text-[11px] text-text-secondary">
                                            <div className="uppercase tracking-widest text-[10px] mb-1 text-text-main">Compliance</div>
                                            <div>Unsubscribe included: {log.compliance.unsubscribe_included ? "✓" : "✗"}</div>
                                            <div>Tokens generated: {log.compliance.unsubscribe_tokens_generated}</div>
                                            <div>Opted-out skipped: {(log.compliance.skipped_opted_out || []).length}</div>
                                        </div>
                                    )}
                                    {log.retracted && log.retract_reason && (
                                        <div className="rounded-lg border border-[var(--color-alert)]/30 bg-[var(--color-alert)]/5 p-3 text-[11px] text-[var(--color-alert)]/90">
                                            Retracted at {(log.retracted_at || "").slice(0, 19).replace("T", " ")} — {log.retract_reason}
                                        </div>
                                    )}
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                    {canSendNow && (
                        <button
                            data-testid={`email-send-dispatch-due-btn-${log.id}`}
                            onClick={() => onDispatchDue(log)}
                            disabled={disabled}
                            className="text-[10px] rounded-full bg-[#A3A3A3] text-black hover:bg-[#A3A3A3]/90 px-3 py-1 font-medium flex items-center gap-1 disabled:opacity-40"
                        >
                            <Send className="h-3 w-3" /> Send now
                        </button>
                    )}
                    {canCancel && (
                        <button
                            data-testid={`email-send-cancel-btn-${log.id}`}
                            onClick={() => onCancel(log)}
                            disabled={disabled}
                            className="text-[10px] rounded-full border border-[var(--color-alert)]/40 text-[var(--color-alert)] px-3 py-1 hover:bg-[var(--color-alert)]/10 flex items-center gap-1 disabled:opacity-40"
                        >
                            <Ban className="h-3 w-3" /> Cancel
                        </button>
                    )}
                    {canRetract && (
                        <button
                            data-testid={`email-send-retract-btn-${log.id}`}
                            onClick={() => onRetract(log)}
                            disabled={disabled}
                            className="text-[10px] rounded-full border border-[var(--color-border)] text-text-secondary hover:text-[var(--color-alert)] px-3 py-1 flex items-center gap-1"
                        >
                            <RotateCcw className="h-3 w-3" /> Retract
                        </button>
                    )}
                </div>
            </div>
        </li>
    );
}


// ---- rollout banner (Phase 16a) -------------------------------------------

export const STEP_INFO = {
    1: { name: "Admin-only dispatch", desc: "Recipients must be in ADMIN_EMAILS. Founder smoke-test phase." },
    2: { name: "Tiny segment (≤10)", desc: "Beta cohort — up to 10 recipients per call." },
    3: { name: "Capped segment (≤100)", desc: "Standard Fase 15 behaviour. Full 100-cap." },
    4: { name: "Full rollout", desc: "Live delivery active (requires production_ready + key + sandbox off)." },
};

export function StepDot({ n, current }) {
    const active = n <= current;
    const isCurrent = n === current;
    return (
        <div
            data-testid={`rollout-step-dot-${n}${isCurrent ? "-current" : ""}`}
            className="flex-1 flex flex-col items-center gap-1 min-w-0"
        >
            <div className={`h-2.5 w-2.5 rounded-full transition-colors ${
                isCurrent
                    ? "bg-white hover:bg-[#EDEDED] ring-2 ring-[var(--color-secondary)]/40"
                    : active
                        ? "bg-[var(--color-primary)]"
                        : "bg-[var(--color-border)]"
            }`} />
            <span className={`text-[9px] uppercase tracking-widest ${
                isCurrent ? "text-text-main font-semibold" : "text-text-secondary"
            }`}>
                Step {n}
            </span>
        </div>
    );
}

export function RolloutBanner({ rollout, isAdmin, onAdvance, onRollback, busy }) {
    if (!rollout) {
        return null;
    }
    const step = rollout.current_step || 1;
    const isLive = rollout.effective_mode === "live";
    const keyOn = rollout.resend_key_configured;

    return (
        <div
            data-testid="email-send-rollout-banner"
            className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-5 mb-6"
        >
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-4">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                        <Shield className="h-4 w-4 text-[var(--color-primary)]" />
                        <span className="text-[10px] uppercase tracking-widest text-[var(--color-primary)] font-semibold">
                            Phase 16a · Production Rollout
                        </span>
                    </div>
                    <h3 className="text-lg font-semibold text-text-main">
                        Step {step}/4 · {(STEP_INFO[step] || {}).name || rollout.step_name}
                    </h3>
                    <p className="text-xs text-text-secondary mt-1 max-w-2xl">
                        {(STEP_INFO[step] || {}).desc || rollout.step_description}
                    </p>
                </div>
                <div className="flex flex-wrap gap-2 shrink-0">
                    {!keyOn ? (
                        <span
                            data-testid="rollout-key-badge-none"
                            className="text-[10px] uppercase tracking-widest px-3 py-1 rounded-full border border-[var(--color-alert)]/40 text-[var(--color-alert)] bg-[var(--color-alert)]/10 flex items-center gap-1"
                        >
                            <Key className="h-3 w-3" /> No key
                        </span>
                    ) : isLive ? (
                        <span
                            data-testid="rollout-key-badge-live"
                            className="text-[10px] uppercase tracking-widest px-3 py-1 rounded-full border border-[var(--color-success)]/40 text-[var(--color-success)] bg-[var(--color-success)]/10 flex items-center gap-1"
                        >
                            <Zap className="h-3 w-3" /> LIVE key ({rollout.resend_key_masked})
                        </span>
                    ) : (
                        <span
                            data-testid="rollout-key-badge-configured"
                            className="text-[10px] uppercase tracking-widest px-3 py-1 rounded-full border border-[#A3A3A3]/40 text-[#A3A3A3] bg-[#A3A3A3]/10 flex items-center gap-1"
                        >
                            <Key className="h-3 w-3" /> Configured ({rollout.resend_key_masked})
                        </span>
                    )}
                    <span
                        data-testid={`rollout-mode-badge-${rollout.effective_mode}`}
                        className={`text-[10px] uppercase tracking-widest px-3 py-1 rounded-full border flex items-center gap-1 ${
                            isLive
                                ? "border-white/15 text-white bg-white/5"
                                : "border-[#A3A3A3]/40 text-[#A3A3A3] bg-[#A3A3A3]/10"
                        }`}
                    >
                        {isLive ? <Rocket className="h-3 w-3" /> : <FlaskConical className="h-3 w-3" />}
                        {isLive ? "LIVE" : "Sandbox forced"}
                    </span>
                </div>
            </div>

            {/* Progress dots */}
            <div
                data-testid="rollout-progress-dots"
                className="flex items-center justify-between gap-2 rounded-xl bg-[var(--color-panel-ai)] px-4 py-3 mb-3"
            >
                {[1, 2, 3, 4].map((n) => (
                    <React.Fragment key={n}>
                        <StepDot n={n} current={step} />
                        {n < 4 && (
                            <div className={`h-px flex-1 max-w-16 ${
                                n < step
                                    ? "bg-[var(--color-primary)]"
                                    : "bg-[var(--color-border)]"
                            }`} />
                        )}
                    </React.Fragment>
                ))}
            </div>

            {/* Blockers */}
            {rollout.blockers && rollout.blockers.length > 0 && !isLive && (
                <div
                    data-testid="rollout-blockers"
                    className="rounded-lg border border-[#A3A3A3]/30 bg-[#A3A3A3]/5 px-3 py-2 text-[11px] text-[#A3A3A3]/90 mb-3"
                >
                    <div className="uppercase tracking-widest text-[10px] mb-1 flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" /> Blockers preventing live mode:
                    </div>
                    <ul className="space-y-0.5">
                        {rollout.blockers.map((b) => (
                            <li key={b} className="font-mono">· {b}</li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Advance / rollback buttons */}
            {isAdmin && (
                <div className="flex flex-wrap gap-2 items-center">
                    <button
                        data-testid="rollout-rollback-btn"
                        onClick={onRollback}
                        disabled={!rollout.previous_step_available || busy}
                        className="text-xs rounded-full border border-[var(--color-border)] text-text-secondary hover:text-text-main px-4 py-2 flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        <ChevronRight className="h-3 w-3 rotate-180" />
                        Rollback to Step {Math.max(1, step - 1)}
                    </button>
                    <button
                        data-testid="rollout-advance-btn"
                        onClick={onAdvance}
                        disabled={!rollout.next_step_available || busy}
                        className="text-xs rounded-full bg-white hover:bg-white/90 text-black px-5 py-2 font-medium flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        Advance to Step {Math.min(4, step + 1)}
                        <ChevronRight className="h-3 w-3" />
                    </button>
                    <span className="text-[10px] text-text-secondary ml-1">
                        ±1 step transitions only · audited
                    </span>
                </div>
            )}
        </div>
    );
}

